import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { uploadInventoryFile, saveInventoryMappingConfig, ingestInventoryRecords } from './api';
import CountryPicker from './CountryPicker';
import { useUser } from '../context/UserContext';

const DRAFT_KEY = 'inventoryUploadDraft';

const CANONICAL_FIELDS = [
  { key: 'facilityCode', label: 'Item Code', required: true, type: 'string', description: 'Unique identifier for the cold chain equipment item' },
  { key: 'facilityName', label: 'Facility Name', required: true, type: 'string', description: 'Name of the health facility or storage location' },
  { key: 'provinceState', label: 'Province/State', required: true, type: 'string', description: 'Administrative division (province, state, or region)' },
  { key: 'districtLga', label: 'District/LGA', required: true, type: 'string', description: 'Local government area or district' },
  { key: 'deviceType', label: 'Device Type', required: true, type: 'string', description: 'Type of cold chain equipment (refrigerator, freezer, cold room, etc.)' },
  { key: 'energySource', label: 'Energy Source', required: true, type: 'string', description: 'Power source: Solar, Grid, Generator, or Other' },
  { key: 'manufacturer', label: 'Manufacturer', required: true, type: 'string', description: 'Equipment manufacturer or brand name' },
  { key: 'model', label: 'Model', required: true, type: 'string', description: 'Equipment model or series name' },
  { key: 'serialNumber', label: 'Serial Number', required: true, type: 'string', description: 'Unique serial number of the equipment' },
  { key: 'status', label: 'Status', required: true, type: 'string', description: 'Operational status (working, broken, under maintenance, etc.)' },
  { key: 'installDate', label: 'Install Date', required: true, type: 'date', description: 'Year or full date when equipment was installed' },
  { key: 'capacity', label: 'Capacity (L)', required: true, type: 'number', description: 'Storage capacity in liters' },
  { key: 'latitude', label: 'Latitude', required: false, type: 'number', description: 'Geographic latitude coordinate (optional)' },
  { key: 'longitude', label: 'Longitude', required: false, type: 'number', description: 'Geographic longitude coordinate (optional)' }
];

function normalizeHeader(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function guessMapping(headers) {
  const normToOriginal = {};
  headers.forEach(h => { normToOriginal[normalizeHeader(h)] = h; });
  const guesses = {};
  const rules = {
    facilityCode: ['facility code', 'hf code', 'facility id', 'site code'],
    facilityName: ['facility name', 'site name', 'health facility name', 'hf name'],
    provinceState: ['province', 'state', 'region', 'province/state', 'state/province'],
    districtLga: ['district', 'lga', 'county', 'municipality', 'district/lga'],
    deviceType: ['device type', 'equipment type', 'equipment'],
    energySource: ['energy source', 'power source', 'energysource', 'power'],
    manufacturer: ['manufacturer', 'make', 'brand'],
    model: ['model', 'model name'],
    serialNumber: ['serial number', 'serial', 'sn'],
    status: ['status', 'condition', 'operational status'],
    installDate: ['install date', 'installation date', 'installed on', 'date installed'],
    capacity: ['capacity', 'volume', 'liters', 'l'],
    latitude: ['latitude', 'lat'],
    longitude: ['longitude', 'lng', 'lon']
  };
  CANONICAL_FIELDS.forEach(f => {
    for (const candidate of rules[f.key] || []) {
      const norm = normalizeHeader(candidate);
      const match = Object.keys(normToOriginal).find(h => h === norm);
      if (match) { guesses[f.key] = normToOriginal[match]; break; }
    }
  });
  return guesses;
}

function parseFileSample(file, setState) {
  const ext = file.name.split('.').pop().toLowerCase();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = (e) => reject(e);
    reader.onload = () => {
      try {
        if (ext === 'csv') {
          const text = reader.result;
          const parsed = Papa.parse(text, { header: true, dynamicTyping: false, skipEmptyLines: true });
          const rows = parsed.data || [];
          const headers = parsed.meta?.fields || Object.keys(rows[0] || {});
          resolve({ rows: rows.slice(0, 200), headers });
        } else if (ext === 'xlsx' || ext === 'xls') {
          const workbook = XLSX.read(reader.result, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          const headers = Object.keys(json[0] || {});
          resolve({ rows: json.slice(0, 200), headers });
        } else {
          reject(new Error('Unsupported file type. Please use CSV or XLSX.'));
        }
      } catch (err) {
        reject(err);
      }
    };
    if (ext === 'csv') reader.readAsText(file);
    else reader.readAsArrayBuffer(file);
  });
}

function downloadCsv(filename, rows) {
  const header = Object.keys(rows[0] || {});
  const lines = [header.join(','), ...rows.map(r => header.map(h => JSON.stringify(r[h] ?? '').replace(/^"|"$/g, '"')).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function UploadMappingWizard() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [types, setTypes] = useState({});
  const [dateFormat, setDateFormat] = useState('YYYY-MM-DD');
  const [unitConversions, setUnitConversions] = useState({ capacity: 'L' });
  const [errors, setErrors] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [countryOption, setCountryOption] = useState(null);

  const fileInputRef = useRef(null);
  const { user } = useUser();

  // Load draft
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        setStep(d.step ?? 0);
        setHeaders(d.headers ?? []);
        setSampleRows(d.sampleRows ?? []);
        setMapping(d.mapping ?? {});
        setTypes(d.types ?? {});
        setDateFormat(d.dateFormat ?? 'YYYY-MM-DD');
        setUnitConversions(d.unitConversions ?? { capacity: 'L' });
      }
    } catch {}
  }, []);

  const saveDraft = useCallback(() => {
    const draft = { step, headers, sampleRows, mapping, types, dateFormat, unitConversions };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [step, headers, sampleRows, mapping, types, dateFormat, unitConversions]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(DRAFT_KEY);
  }, []);

  const mappedColumnsOptions = useMemo(() => ['— Ignore —', ...headers], [headers]);

  const onFileSelected = async (f) => {
    setFile(f);
    const { rows, headers: hdrs } = await parseFileSample(f, setSampleRows);
    setSampleRows(rows);
    setHeaders(hdrs);
    const g = guessMapping(hdrs);
    setMapping(g);
    const t = {};
    CANONICAL_FIELDS.forEach(cf => { t[cf.key] = cf.type; });
    setTypes(t);
    setStep(1);
  };

  const handleValidate = () => {
    const errs = [];
    const mapped = CANONICAL_FIELDS
      .filter(f => mapping[f.key] && mapping[f.key] !== '— Ignore —')
      .map(f => ({ field: f, source: mapping[f.key] }));

    sampleRows.forEach((row, idx) => {
      mapped.forEach(({ field, source }) => {
        const val = row[source];
        if (field.required && (val === undefined || val === null || String(val).trim() === '')) {
          errs.push({ row: idx + 2, field: field.label, issue: 'Required' });
        }
        if (types[field.key] === 'number' && val !== undefined && val !== null && String(val).trim() !== '' && isNaN(Number(val))) {
          errs.push({ row: idx + 2, field: field.label, issue: 'Must be a number' });
        }
        if (['latitude', 'longitude'].includes(field.key) && String(val).trim() !== '') {
          const num = Number(val);
          if (field.key === 'latitude' && (num < -90 || num > 90)) errs.push({ row: idx + 2, field: field.label, issue: 'Out of range (-90..90)' });
          if (field.key === 'longitude' && (num < -180 || num > 180)) errs.push({ row: idx + 2, field: field.label, issue: 'Out of range (-180..180)' });
        }
      });
    });

    setErrors(errs);

    // Build preview rows in canonical shape
    const prev = sampleRows.map(row => {
      const out = {};
      CANONICAL_FIELDS.forEach(f => {
        const s = mapping[f.key];
        if (s && s !== '— Ignore —') out[f.key] = row[s];
      });
      return out;
    });
    setPreviewRows(prev);
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!file) return;
    if (!countryOption || !countryOption.iso3) {
      setSubmitResult({ ok: false, error: 'Please select a country' });
      return;
    }
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const uploaded = await uploadInventoryFile(file);
      const config = {
        file: { name: file.name, path: uploaded.blobName || uploaded.path || uploaded.url },
        mapping,
        types,
        dateFormat,
        unitConversions,
        canonicalFields: CANONICAL_FIELDS,
        createdAt: new Date().toISOString()
      };
      const saved = await saveInventoryMappingConfig(config);
      // Parse full file to canonical records
      const parseFullFile = async (f) => {
        const ext = f.name.split('.').pop().toLowerCase();
        const buffer = await f.arrayBuffer();
        if (ext === 'csv') {
          const text = new TextDecoder('utf-8').decode(new Uint8Array(buffer));
          const parsed = Papa.parse(text, { header: true, dynamicTyping: false, skipEmptyLines: true });
          return parsed.data || [];
        }
        if (ext === 'xlsx' || ext === 'xls') {
          const workbook = XLSX.read(buffer, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          return XLSX.utils.sheet_to_json(worksheet, { defval: '' });
        }
        throw new Error('Unsupported file type');
      };

      const rawRows = await parseFullFile(file);
      const canonicalRows = rawRows.map(row => {
        const out = {};
        CANONICAL_FIELDS.forEach(f => {
          const s = mapping[f.key];
          if (s && s !== '— Ignore —') out[f.key] = row[s];
        });
        // Normalize capacity
        if (out.capacity !== undefined && out.capacity !== null && String(out.capacity).trim() !== '') {
          const num = Number(out.capacity);
          if (!Number.isNaN(num)) {
            out.capacityLiters = unitConversions.capacity === 'm3' ? num * 1000 : num;
          }
        }
        return out;
      });

      // Ingest into backend DB
      const payload = {
        uploadGroupId: Date.now(),
        countryIso3: countryOption.iso3,
        userId: user?.id || null,
        userEmail: user?.email || null,
        originalFilename: file.name,
        blobContainer: 'inventories',
        blobName: uploaded.blobName || uploaded.path || uploaded.url,
        mappingBlobName: saved.blob,
        records: canonicalRows
      };
      const ingestRes = await ingestInventoryRecords(payload);

      setSubmitResult({ ok: true, uploaded, saved, ingest: ingestRes });
      clearDraft();
      setStep(3);
    } catch (e) {
      setSubmitResult({ ok: false, error: e.message || String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const StepHeader = () => (
    <div className="flex items-center justify-between mb-6">
      <div className="flex-1 grid grid-cols-4 gap-2">
        {[ 'Upload', 'Map Fields', 'Validate & Preview', 'Submit' ].map((label, idx) => (
          <div key={label} className={`px-3 py-2 rounded text-sm text-center ${step === idx ? 'bg-blue-600 text-white' : idx < step ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
            {label}
          </div>
        ))}
      </div>
      <div className="ml-4 flex items-center space-x-2">
        <button className="px-3 py-2 border rounded text-sm hover:bg-gray-50" onClick={saveDraft}>Save draft</button>
        <button className="px-3 py-2 border rounded text-sm hover:bg-gray-50" onClick={() => { localStorage.removeItem(DRAFT_KEY); window.location.reload(); }}>Reset</button>
      </div>
    </div>
  );

  return (
    <div>
      <StepHeader />

      {step === 0 && (
        <div className="border rounded p-6 bg-gray-50">
          <div className="text-gray-700 mb-4">Drop a CSV/XLSX file or browse.</div>
          <div className="flex items-center space-x-3">
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => e.target.files && e.target.files[0] && onFileSelected(e.target.files[0])} />
            <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={() => fileInputRef.current?.click()}>Choose file</button>
            {file && <span className="text-sm text-gray-600">{file.name}</span>}
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <div className="overflow-x-auto">
            <table className="min-w-full border">
              <thead>
                <tr>
                  <th className="px-3 py-2 border bg-gray-100 text-left">Destination Column</th>
                  <th className="px-3 py-2 border bg-gray-100 text-left">Source column (Your Data)</th>
                  <th className="px-3 py-2 border bg-gray-100 text-left">Type</th>
                </tr>
              </thead>
              <tbody>
                {CANONICAL_FIELDS.map(cf => (
                  <tr key={cf.key}>
                    <td className="px-3 py-2 border">
                      <div className="font-medium">{cf.label}{cf.required ? ' *' : ''}</div>
                      <div className="text-xs text-gray-500">{cf.description || cf.key}</div>
                    </td>
                    <td className="px-3 py-2 border">
                      <select className="border rounded px-2 py-1 w-full" value={mapping[cf.key] || ''} onChange={e => setMapping(m => ({ ...m, [cf.key]: e.target.value || '— Ignore —' }))}>
                        {mappedColumnsOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                      <div className="text-xs text-gray-600 leading-relaxed mt-2 max-w-xs">
                        {(() => {
                          const src = mapping[cf.key];
                          if (!src || src === '— Ignore —') return <span className="italic text-gray-400">No column selected</span>;
                          const values = sampleRows.slice(0, 5).map(r => r?.[src]).filter(v => v !== undefined && v !== null && String(v).trim() !== '');
                          if (values.length === 0) return <span className="italic text-gray-400">No sample</span>;
                          return (
                            <div className="truncate" title={values.join(', ')}>
                              {values.join(', ')}
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2 border">
                      <select className="border rounded px-2 py-1" value={types[cf.key] || cf.type} onChange={e => setTypes(t => ({ ...t, [cf.key]: e.target.value }))}>
                        <option value="string">String</option>
                        <option value="number">Number</option>
                        <option value="date">Date</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center space-x-3 mt-4">
            <div>
              <label className="text-sm text-gray-600 mr-2">Date format</label>
              <input className="border rounded px-2 py-1" value={dateFormat} onChange={e => setDateFormat(e.target.value)} />
            </div>
            <div>
              <label className="text-sm text-gray-600 mr-2">Capacity unit</label>
              <select className="border rounded px-2 py-1" value={unitConversions.capacity} onChange={e => setUnitConversions(u => ({ ...u, capacity: e.target.value }))}>
                <option value="L">L</option>
                <option value="m3">m3</option>
              </select>
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button className="px-4 py-2 border rounded" onClick={() => setStep(0)}>Back</button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={handleValidate}>Validate & Preview</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          {errors.length > 0 && (
            <div className="mb-4 p-3 border border-yellow-300 bg-yellow-50 text-yellow-800 rounded">
              <div className="font-medium mb-2">Found {errors.length} issues</div>
              <div className="max-h-48 overflow-auto text-sm">
                <table className="min-w-full">
                  <thead>
                    <tr>
                      <th className="text-left pr-3">Row</th>
                      <th className="text-left pr-3">Field</th>
                      <th className="text-left">Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errors.slice(0, 200).map((e, i) => (
                      <tr key={i} className="align-top">
                        <td className="pr-3">{e.row}</td>
                        <td className="pr-3">{e.field}</td>
                        <td>{e.issue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2">
                <button className="px-3 py-1 border rounded text-sm" onClick={() => downloadCsv('inventory-errors.csv', errors)}>Download error report</button>
              </div>
            </div>
          )}

          <div className="border rounded">
            <div className="px-3 py-2 bg-gray-100 border-b font-medium">Preview (first {previewRows.length} rows)</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left">
                <thead>
                  <tr>
                    {CANONICAL_FIELDS.filter(f => mapping[f.key] && mapping[f.key] !== '— Ignore —').map(f => (
                      <th key={f.key} className="px-3 py-2 border bg-gray-50">{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.slice(0, 20).map((r, i) => (
                    <tr key={i}>
                      {CANONICAL_FIELDS.filter(f => mapping[f.key] && mapping[f.key] !== '— Ignore —').map(f => (
                        <td key={f.key} className="px-3 py-2 border">{String(r[f.key] ?? '')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-between mt-6">
            <button className="px-4 py-2 border rounded" onClick={() => setStep(1)}>Back</button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={() => setStep(3)}>Continue</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="max-w-xl">
          <div className="mb-4 text-gray-700">Submit will save the original file and your mapping configuration. A backend worker can process and upsert later.</div>
          <div className="space-y-2 text-sm text-gray-600">
            <div><span className="font-medium">File:</span> {file?.name}</div>
            <div><span className="font-medium">Mapped fields:</span> {Object.entries(mapping).filter(([, v]) => v && v !== '— Ignore —').length}</div>
          </div>
          <div className="mt-4">
            <label className="block text-sm text-gray-600 mb-1">Country</label>
            <CountryPicker value={countryOption} onChange={setCountryOption} isMulti={false} />
          </div>
          <div className="flex items-center space-x-3 mt-6">
            <button className="px-4 py-2 border rounded" onClick={() => setStep(2)}>Back</button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50" disabled={submitting} onClick={handleSubmit}>{submitting ? 'Submitting…' : 'Submit'}</button>
            <button className="px-4 py-2 border rounded" onClick={saveDraft}>Save draft</button>
          </div>
          {submitResult && (
            <div className={`mt-4 p-3 rounded ${submitResult.ok ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
              {submitResult.ok ? 'Saved successfully.' : `Failed: ${submitResult.error}`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


